import { useAuth0 } from '@auth0/auth0-react'
import { UseMutationOptions, UseMutationResult, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { QueryKeys } from '../../types'
import { createCase, getCase, addMessage, markMessageAsRead } from './portalCase'
import { CaseViewModel } from './types'

export const useCreateCaseMutation = (
  options?: UseMutationOptions<CaseViewModel, Error, { message: string }, unknown>
): UseMutationResult<CaseViewModel, Error, { message: string }, unknown> => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.PortalCase, 'create'],
    mutationFn: async ({ message }) => {
      const accessToken = await getAccessTokenSilently()
      return await createCase({ accessToken, message })
    },
    ...options,
  })
}

export const useGetCaseQuery = (caseId: string | undefined): UseQueryResult<CaseViewModel, Error> => {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery({
    queryKey: [QueryKeys.PortalCase, caseId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getCase({ accessToken, caseId: caseId! })
    },
    enabled: !!caseId,
    retry: false,
  })
}

export const useMarkMessageAsReadMutation = (
  options?: UseMutationOptions<void, Error, { messageId: string }, unknown>
): UseMutationResult<void, Error, { messageId: string }, unknown> => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.PortalCase, 'markAsRead'],
    mutationFn: async ({ messageId }) => {
      const accessToken = await getAccessTokenSilently()
      return await markMessageAsRead({ accessToken, messageId })
    },
    ...options,
  })
}

export const useAddMessageMutation = (
  options?: UseMutationOptions<CaseViewModel, Error, { caseId: string; message: string }, unknown>
): UseMutationResult<CaseViewModel, Error, { caseId: string; message: string }, unknown> => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationKey: [QueryKeys.PortalCase, 'addMessage'],
    mutationFn: async ({ caseId, message }) => {
      const accessToken = await getAccessTokenSilently()
      return await addMessage({ accessToken, caseId, message })
    },
    ...options,
  })
}
